import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  IconButton,
  Tooltip,
  Drawer,
  Divider,
  Dialog,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
  Fade,
} from '@mui/material';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import GridOnIcon from '@mui/icons-material/GridOn';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import RoofingIcon from '@mui/icons-material/Roofing';
import ViewInArIcon from '@mui/icons-material/ViewInAr';
import VerticalShadesIcon from '@mui/icons-material/VerticalShades';
import TerrainIcon from '@mui/icons-material/Terrain';
import FoundationIcon from '@mui/icons-material/Foundation';
import FormatPaintIcon from '@mui/icons-material/FormatPaint';
import TranslateIcon from '@mui/icons-material/Translate';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import CalculateIcon from '@mui/icons-material/Calculate';
import SpeedIcon from '@mui/icons-material/Speed';
import InventoryIcon from '@mui/icons-material/Inventory';
import SavingsIcon from '@mui/icons-material/Savings';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import TouchAppIcon from '@mui/icons-material/TouchApp';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useLanguage } from '../../utils/LanguageContext';
import { MaterialEstimator } from '../estimator';
import { Estimator } from '../estimator';

const LandingPage = ({ darkMode, toggleDarkMode }) => {
  const navigate = useNavigate();
  const { language, changeLanguage } = useLanguage();
  const [calculatorOpen, setCalculatorOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showBudget, setShowBudget] = useState(false);
  const [tutorialOpen, setTutorialOpen] = useState(false);
  const [tutorialStep, setTutorialStep] = useState(0);

  // Check if user has seen tutorial
  useEffect(() => {
    const hasSeenTutorial = localStorage.getItem('rumahkira_tutorial_seen');
    if (!hasSeenTutorial) {
      // Small delay to let the page render first
      const timer = setTimeout(() => {
        setTutorialOpen(true);
      }, 800);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleTutorialClose = () => {
    setTutorialOpen(false);
    setTutorialStep(0);
    localStorage.setItem('rumahkira_tutorial_seen', 'true');
  };

  const handleTutorialNext = () => {
    if (tutorialStep < 4) {
      setTutorialStep(prev => prev + 1);
    } else {
      handleTutorialClose();
    }
  };

  const handleTutorialBack = () => {
    if (tutorialStep > 0) {
      setTutorialStep(prev => prev - 1);
    }
  };

  const handleOpenTutorial = () => {
    setTutorialStep(0);
    setTutorialOpen(true);
  };

  const handleLanguageToggle = () => {
    changeLanguage(language === 'en' ? 'my' : 'en');
  };

  const content = {
    en: {
      tagline: 'Professional Construction Cost Estimator',
      subtitle: 'Plan your dream home budget with confidence',
      signIn: 'Sign In',

      // Groups
      structureGroup: 'Structure',
      finishesGroup: 'Finishes',

      // Categories
      walls: 'Walls',
      floors: 'Floors',
      roofing: 'Roofing',
      concrete: 'Concrete',
      ceiling: 'Ceiling',
      earthwork: 'Earthwork',
      foundation: 'Foundation',
      plastering: 'Plastering',

      // Budget
      budgetEst: 'Budget Estimator',
      budgetDesc: 'Complete house cost estimation',

      // Features
      featuresTitle: 'What You Can Do',
      feature1Title: 'Material Calculator',
      feature1Desc: 'Calculate bricks, cement, sand, tiles and more with precision',
      feature2Title: 'Instant Results',
      feature2Desc: 'Get accurate estimates in seconds, not hours',
      feature3Title: 'Bundle & Unit Info',
      feature3Desc: 'See quantities in bundles, bags, m³ or cubic feet',
      feature4Title: 'Budget Planning',
      feature4Desc: 'Plan your complete house construction budget',

      // About
      aboutTitle: 'About RumahKira',
      aboutDesc: 'RumahKira is a professional construction cost estimation tool designed for homeowners, contractors, and builders in Malaysia. Calculate material quantities accurately for your house construction projects - from foundation to roofing.',
      aboutHighlight: 'Free to use. No registration required to try.',

      // Footer
      footerText: 'RumahKira™ - House Construction Budgeting App',
      footerSubtext: 'A digital estimation tool by',
      companyName: 'Kondingau Community Insight (KCi)',

      // Tutorial
      tutorialTitle: 'Welcome to RumahKira!',
      tutorialSkip: 'Skip',
      tutorialNext: 'Next',
      tutorialBack: 'Back',
      tutorialDone: 'Start Using',
      tutorialStep1Title: 'Choose a Category',
      tutorialStep1Desc: 'Tap any category icon to calculate materials. Each category contains different types of materials.',
      tutorialStep1Tip: 'Tip: "Walls" for bricks, "Floors" for tiles, "Concrete" for slabs & columns',
      tutorialStep2Title: 'Select Material Type',
      tutorialStep2Desc: 'Choose the specific material you want to calculate. Don\'t worry about technical names - just pick what matches your project.',
      tutorialStep2Tip: 'Tip: For beginners, choose options labeled "Direct Area" for simpler input',
      tutorialStep3Title: 'Enter Measurements',
      tutorialStep3Desc: 'Fill in your measurements. You can change units (feet, meters, inches) using the unit selector at the top.',
      tutorialStep3Tip: 'Tip: Measure your wall/floor length and width. The app will calculate everything else!',
      tutorialStep4Title: 'Get Your Results',
      tutorialStep4Desc: 'See exactly what materials you need! Results show quantities in multiple units (bags, m³, tons) so you can easily order from suppliers.',
      tutorialStep4Tip: 'Tip: The "ton" measurement is useful when ordering sand/stone by lorry',
      tutorialStep5Title: 'You\'re Ready!',
      tutorialStep5Desc: 'Start calculating materials for your dream home. Tap the Help button anytime to see this guide again.',
      help: 'Help',
    },
    my: {
      tagline: 'Penganggar Kos Pembinaan Profesional',
      subtitle: 'Rancang bajet rumah impian anda dengan yakin',
      signIn: 'Log Masuk',

      // Groups
      structureGroup: 'Struktur',
      finishesGroup: 'Kemasan',

      // Categories
      walls: 'Dinding',
      floors: 'Lantai',
      roofing: 'Bumbung',
      concrete: 'Konkrit',
      ceiling: 'Siling',
      earthwork: 'Kerja Tanah',
      foundation: 'Asas',
      plastering: 'Plaster',

      // Budget
      budgetEst: 'Penganggar Bajet',
      budgetDesc: 'Anggaran kos rumah lengkap',

      // Features
      featuresTitle: 'Apa Yang Boleh Dilakukan',
      feature1Title: 'Kalkulator Bahan',
      feature1Desc: 'Kira bata, simen, pasir, jubin dan lain-lain dengan tepat',
      feature2Title: 'Keputusan Segera',
      feature2Desc: 'Dapatkan anggaran tepat dalam saat, bukan jam',
      feature3Title: 'Info Berkas & Unit',
      feature3Desc: 'Lihat kuantiti dalam berkas, beg, m³ atau kaki padu',
      feature4Title: 'Perancangan Bajet',
      feature4Desc: 'Rancang bajet pembinaan rumah lengkap anda',

      // About
      aboutTitle: 'Tentang RumahKira',
      aboutDesc: 'RumahKira adalah alat anggaran kos pembinaan profesional yang direka untuk pemilik rumah, kontraktor, dan pembina di Malaysia. Kira kuantiti bahan dengan tepat untuk projek pembinaan rumah anda - dari asas hingga bumbung.',
      aboutHighlight: 'Percuma untuk digunakan. Tiada pendaftaran diperlukan untuk mencuba.',

      // Footer
      footerText: 'RumahKira™ - Aplikasi Belanjawan Pembinaan Rumah',
      footerSubtext: 'Alat anggaran digital oleh',
      companyName: 'Kondingau Community Insight (KCi)',

      // Tutorial
      tutorialTitle: 'Selamat Datang ke RumahKira!',
      tutorialSkip: 'Langkau',
      tutorialNext: 'Seterusnya',
      tutorialBack: 'Kembali',
      tutorialDone: 'Mula Guna',
      tutorialStep1Title: 'Pilih Kategori',
      tutorialStep1Desc: 'Klik mana-mana ikon kategori untuk mengira bahan. Setiap kategori mengandungi jenis bahan yang berbeza.',
      tutorialStep1Tip: 'Tip: "Dinding" untuk bata, "Lantai" untuk jubin, "Konkrit" untuk papak & tiang',
      tutorialStep2Title: 'Pilih Jenis Bahan',
      tutorialStep2Desc: 'Pilih bahan spesifik yang anda mahu kira. Jangan risau tentang nama teknikal - pilih sahaja yang sepadan dengan projek anda.',
      tutorialStep2Tip: 'Tip: Untuk pemula, pilih pilihan berlabel "Luas Terus" untuk input lebih mudah',
      tutorialStep3Title: 'Masukkan Ukuran',
      tutorialStep3Desc: 'Isikan ukuran anda. Anda boleh tukar unit (kaki, meter, inci) menggunakan pemilih unit di bahagian atas.',
      tutorialStep3Tip: 'Tip: Ukur panjang dan lebar dinding/lantai anda. Aplikasi akan mengira yang lain!',
      tutorialStep4Title: 'Dapatkan Keputusan',
      tutorialStep4Desc: 'Lihat dengan tepat bahan yang anda perlukan! Keputusan menunjukkan kuantiti dalam pelbagai unit (beg, m³, tan) supaya anda boleh memesan dari pembekal dengan mudah.',
      tutorialStep4Tip: 'Tip: Ukuran "tan" berguna semasa memesan pasir/batu dengan lori',
      tutorialStep5Title: 'Anda Sudah Sedia!',
      tutorialStep5Desc: 'Mula mengira bahan untuk rumah impian anda. Klik butang Bantuan bila-bila masa untuk melihat panduan ini lagi.',
      help: 'Bantuan',
    }
  };

  const t = content[language] || content.en;

  // Grouped categories
  const categoryGroups = [
    {
      id: 'structure',
      label: t.structureGroup,
      categories: [
        { id: 'foundation', icon: <FoundationIcon />, label: t.foundation, color: '#059669' },
        { id: 'concrete', icon: <ViewInArIcon />, label: t.concrete, color: '#6B7280' },
        { id: 'walls', icon: <GridOnIcon />, label: t.walls, color: '#4F46E5' },
        { id: 'ceiling', icon: <VerticalShadesIcon />, label: t.ceiling, color: '#8B5CF6' },
      ]
    },
    {
      id: 'finishes',
      label: t.finishesGroup,
      categories: [
        { id: 'floors', icon: <ViewModuleIcon />, label: t.floors, color: '#0891B2' },
        { id: 'roofing', icon: <RoofingIcon />, label: t.roofing, color: '#DC2626' },
        { id: 'plastering', icon: <FormatPaintIcon />, label: t.plastering, color: '#EC4899' },
        { id: 'earthwork', icon: <TerrainIcon />, label: t.earthwork, color: '#D97706' },
      ]
    }
  ];

  const handleCategoryClick = (categoryId) => {
    setSelectedCategory(categoryId);
    setShowBudget(false);
    setCalculatorOpen(true);
  };

  const handleBudgetClick = () => {
    setSelectedCategory(null);
    setShowBudget(true);
    setCalculatorOpen(true);
  };

  const handleCloseCalculator = () => {
    setCalculatorOpen(false);
    setSelectedCategory(null);
    setShowBudget(false);
  };

  const handleSignIn = () => {
    navigate('/auth');
  };

  // Category item component
  const CategoryItem = ({ category }) => (
    <Box
      onClick={() => handleCategoryClick(category.id)}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 0.5,
        p: 1.5,
        borderRadius: 2,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        '&:hover': {
          bgcolor: (theme) => theme.palette.mode === 'dark'
            ? 'rgba(255,255,255,0.05)'
            : 'rgba(0,0,0,0.03)',
          transform: 'translateY(-2px)',
        },
        '&:active': {
          transform: 'scale(0.98)',
        },
      }}
    >
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: 2,
          bgcolor: `${category.color}15`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: category.color,
          fontSize: 24,
        }}
      >
        {category.icon}
      </Box>
      <Typography variant="caption" fontWeight={500} color="text.primary">
        {category.label}
      </Typography>
    </Box>
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', pt: '80px' }}>
      {/* Floating Navbar */}
      <Box
        component="nav"
        sx={{
          position: 'fixed',
          top: 12,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 1100,
          width: 'calc(100% - 24px)',
          maxWidth: 600,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            py: 1,
            px: 2,
            borderRadius: 4,
            bgcolor: (theme) => theme.palette.mode === 'dark'
              ? 'rgba(30, 41, 59, 0.9)'
              : 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(20px)',
            boxShadow: (theme) => theme.palette.mode === 'dark'
              ? '0 4px 30px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255,255,255,0.05)'
              : '0 4px 30px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(0,0,0,0.03)',
          }}
        >
          {/* Logo */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 2,
                bgcolor: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
              }}
            >
              <HomeWorkIcon sx={{ fontSize: 20 }} />
            </Box>
            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
              <Typography variant="subtitle2" fontWeight={700} color="text.primary" sx={{ lineHeight: 1.2 }}>
                RumahKira<sup style={{ fontSize: '0.5em' }}>&trade;</sup>
              </Typography>
            </Box>
          </Box>

          {/* Actions */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Tooltip title={t.help}>
              <IconButton
                onClick={handleOpenTutorial}
                size="small"
                sx={{
                  bgcolor: (theme) => theme.palette.mode === 'dark'
                    ? 'rgba(255,255,255,0.05)'
                    : 'rgba(0,0,0,0.04)',
                  '&:hover': {
                    bgcolor: 'primary.main',
                    color: 'white',
                  },
                }}
              >
                <HelpOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={language === 'en' ? 'Bahasa Malaysia' : 'English'}>
              <IconButton
                onClick={handleLanguageToggle}
                size="small"
                sx={{
                  bgcolor: (theme) => theme.palette.mode === 'dark'
                    ? 'rgba(255,255,255,0.05)'
                    : 'rgba(0,0,0,0.04)',
                }}
              >
                <TranslateIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={darkMode ? 'Light Mode' : 'Dark Mode'}>
              <IconButton
                onClick={toggleDarkMode}
                size="small"
                sx={{
                  bgcolor: (theme) => theme.palette.mode === 'dark'
                    ? 'rgba(255,255,255,0.05)'
                    : 'rgba(0,0,0,0.04)',
                }}
              >
                {darkMode ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              size="small"
              startIcon={<PersonIcon />}
              onClick={handleSignIn}
              sx={{
                ml: 0.5,
                borderRadius: 2,
                px: 2,
                boxShadow: 'none',
                '&:hover': { boxShadow: 'none' },
              }}
            >
              {t.signIn}
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Hero Section */}
      <Box
        sx={{
          py: { xs: 4, md: 5 },
          textAlign: 'center',
          background: (theme) =>
            theme.palette.mode === 'dark'
              ? 'linear-gradient(180deg, rgba(99, 102, 241, 0.1) 0%, transparent 100%)'
              : 'linear-gradient(180deg, rgba(79, 70, 229, 0.05) 0%, transparent 100%)',
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant="h4"
            fontWeight={800}
            sx={{ mb: 1, letterSpacing: '-0.02em' }}
          >
            {t.tagline}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t.subtitle}
          </Typography>
        </Container>
      </Box>

      {/* Categories Section */}
      <Container maxWidth="sm" sx={{ py: 4 }}>
        {/* Category Groups */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {categoryGroups.map((group) => (
            <Box
              key={group.id}
              sx={{
                p: 2,
                borderRadius: 3,
                bgcolor: (theme) => theme.palette.mode === 'dark'
                  ? 'rgba(255,255,255,0.03)'
                  : 'rgba(0,0,0,0.02)',
                border: '1px solid',
                borderColor: (theme) => theme.palette.mode === 'dark'
                  ? 'rgba(255,255,255,0.06)'
                  : 'rgba(0,0,0,0.04)',
              }}
            >
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{
                  display: 'block',
                  mb: 1,
                  pl: 1,
                  letterSpacing: '0.1em',
                  fontSize: '0.65rem',
                }}
              >
                {group.label}
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, 1fr)',
                  gap: 0.5,
                }}
              >
                {group.categories.map((category) => (
                  <CategoryItem key={category.id} category={category} />
                ))}
              </Box>
            </Box>
          ))}
        </Box>

        {/* Budget Estimator Card */}
        <Box
          onClick={handleBudgetClick}
          sx={{
            mt: 3,
            p: 2.5,
            borderRadius: 3,
            bgcolor: (theme) => theme.palette.mode === 'dark'
              ? 'rgba(99, 102, 241, 0.1)'
              : 'rgba(79, 70, 229, 0.05)',
            border: '1px solid',
            borderColor: 'primary.main',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            '&:hover': {
              bgcolor: (theme) => theme.palette.mode === 'dark'
                ? 'rgba(99, 102, 241, 0.15)'
                : 'rgba(79, 70, 229, 0.08)',
              transform: 'translateY(-2px)',
            },
            '&:active': {
              transform: 'scale(0.99)',
            },
          }}
        >
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              bgcolor: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
            }}
          >
            <AccountBalanceWalletIcon />
          </Box>
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>
              {t.budgetEst}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t.budgetDesc}
            </Typography>
          </Box>
        </Box>
      </Container>

      {/* Features Section */}
      <Box
        sx={{
          py: 5,
          bgcolor: (theme) => theme.palette.mode === 'dark'
            ? 'rgba(255,255,255,0.02)'
            : 'rgba(0,0,0,0.015)',
        }}
      >
        <Container maxWidth="sm">
          <Typography
            variant="overline"
            color="primary"
            sx={{ display: 'block', textAlign: 'center', mb: 1, letterSpacing: '0.15em' }}
          >
            {t.featuresTitle}
          </Typography>
          <Divider sx={{ mb: 3, mx: 'auto', width: 40 }} />

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
            {/* Feature 1 */}
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 1.5,
                  bgcolor: 'primary.main',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  flexShrink: 0,
                }}
              >
                <CalculateIcon sx={{ fontSize: 18 }} />
              </Box>
              <Box>
                <Typography variant="body2" fontWeight={600} sx={{ mb: 0.25 }}>
                  {t.feature1Title}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4 }}>
                  {t.feature1Desc}
                </Typography>
              </Box>
            </Box>

            {/* Feature 2 */}
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 1.5,
                  bgcolor: '#10B981',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  flexShrink: 0,
                }}
              >
                <SpeedIcon sx={{ fontSize: 18 }} />
              </Box>
              <Box>
                <Typography variant="body2" fontWeight={600} sx={{ mb: 0.25 }}>
                  {t.feature2Title}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4 }}>
                  {t.feature2Desc}
                </Typography>
              </Box>
            </Box>

            {/* Feature 3 */}
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 1.5,
                  bgcolor: '#F59E0B',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  flexShrink: 0,
                }}
              >
                <InventoryIcon sx={{ fontSize: 18 }} />
              </Box>
              <Box>
                <Typography variant="body2" fontWeight={600} sx={{ mb: 0.25 }}>
                  {t.feature3Title}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4 }}>
                  {t.feature3Desc}
                </Typography>
              </Box>
            </Box>

            {/* Feature 4 */}
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 1.5,
                  bgcolor: '#8B5CF6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  flexShrink: 0,
                }}
              >
                <SavingsIcon sx={{ fontSize: 18 }} />
              </Box>
              <Box>
                <Typography variant="body2" fontWeight={600} sx={{ mb: 0.25 }}>
                  {t.feature4Title}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4 }}>
                  {t.feature4Desc}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* About Section */}
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Box
          sx={{
            p: 3,
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            textAlign: 'center',
          }}
        >
          <Typography variant="subtitle2" fontWeight={600} color="primary" sx={{ mb: 1.5 }}>
            {t.aboutTitle}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.7 }}>
            {t.aboutDesc}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              display: 'inline-block',
              px: 2,
              py: 0.75,
              borderRadius: 2,
              bgcolor: (theme) => theme.palette.mode === 'dark'
                ? 'rgba(16, 185, 129, 0.15)'
                : 'rgba(16, 185, 129, 0.1)',
              color: '#10B981',
              fontWeight: 600,
            }}
          >
            {t.aboutHighlight}
          </Typography>
        </Box>
      </Container>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          py: 4,
          mt: 'auto',
          textAlign: 'center',
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: (theme) =>
            theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.02)',
        }}
      >
        <Container>
          <Typography variant="body2" fontWeight={600} color="text.primary" sx={{ mb: 0.5 }}>
            {t.footerText}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t.footerSubtext}{' '}
            <Box component="span" sx={{ fontWeight: 600, color: 'primary.main' }}>
              {t.companyName}
            </Box>
          </Typography>
          <Typography variant="caption" display="block" color="text.disabled" sx={{ mt: 1 }}>
            &copy; {new Date().getFullYear()} Kondingau Community Insight. All rights reserved.
          </Typography>
        </Container>
      </Box>

      {/* Calculator Drawer */}
      <Drawer
        anchor="bottom"
        open={calculatorOpen}
        onClose={handleCloseCalculator}
        PaperProps={{
          sx: {
            maxHeight: '90vh',
            borderRadius: '24px 24px 0 0',
            bgcolor: 'background.paper',
          },
        }}
      >
        {/* Drawer Handle */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            pt: 1.5,
            pb: 1,
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 4,
              borderRadius: 2,
              bgcolor: 'divider',
            }}
          />
        </Box>

        {/* Close Button */}
        <Box sx={{ position: 'absolute', right: 8, top: 8 }}>
          <IconButton onClick={handleCloseCalculator} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Calculator Content */}
        <Box sx={{ overflow: 'auto', maxHeight: 'calc(90vh - 40px)' }}>
          {showBudget ? (
            <Estimator language={language} darkMode={darkMode} />
          ) : (
            <MaterialEstimator language={language} initialCategory={selectedCategory} />
          )}
        </Box>
      </Drawer>

      {/* Tutorial Dialog */}
      <Dialog
        open={tutorialOpen}
        onClose={handleTutorialClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            overflow: 'hidden',
          },
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          {/* Tutorial Header */}
          <Box
            sx={{
              p: 3,
              pb: 2,
              background: (theme) =>
                theme.palette.mode === 'dark'
                  ? 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)'
                  : 'linear-gradient(135deg, #4F46E5 0%, #6366F1 100%)',
              color: 'white',
              textAlign: 'center',
            }}
          >
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: 3,
                bgcolor: 'rgba(255,255,255,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2,
              }}
            >
              {tutorialStep === 0 && <TouchAppIcon sx={{ fontSize: 28 }} />}
              {tutorialStep === 1 && <GridOnIcon sx={{ fontSize: 28 }} />}
              {tutorialStep === 2 && <EditIcon sx={{ fontSize: 28 }} />}
              {tutorialStep === 3 && <CheckCircleIcon sx={{ fontSize: 28 }} />}
              {tutorialStep === 4 && <HomeWorkIcon sx={{ fontSize: 28 }} />}
            </Box>
            <Typography variant="h6" fontWeight={700}>
              {tutorialStep === 0 && t.tutorialStep1Title}
              {tutorialStep === 1 && t.tutorialStep2Title}
              {tutorialStep === 2 && t.tutorialStep3Title}
              {tutorialStep === 3 && t.tutorialStep4Title}
              {tutorialStep === 4 && t.tutorialStep5Title}
            </Typography>
          </Box>

          {/* Step Indicator */}
          <Box sx={{ px: 3, pt: 2 }}>
            <Stepper activeStep={tutorialStep} alternativeLabel>
              {[0, 1, 2, 3, 4].map((step) => (
                <Step key={step}>
                  <StepLabel
                    StepIconProps={{
                      sx: {
                        '&.Mui-active': { color: 'primary.main' },
                        '&.Mui-completed': { color: 'primary.main' },
                      },
                    }}
                  />
                </Step>
              ))}
            </Stepper>
          </Box>

          {/* Tutorial Content */}
          <Box sx={{ p: 3 }}>
            <Fade in={true} key={tutorialStep}>
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.7 }}>
                  {tutorialStep === 0 && t.tutorialStep1Desc}
                  {tutorialStep === 1 && t.tutorialStep2Desc}
                  {tutorialStep === 2 && t.tutorialStep3Desc}
                  {tutorialStep === 3 && t.tutorialStep4Desc}
                  {tutorialStep === 4 && t.tutorialStep5Desc}
                </Typography>

                {/* Tip Box */}
                {tutorialStep < 4 && (
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      bgcolor: (theme) => theme.palette.mode === 'dark'
                        ? 'rgba(245, 158, 11, 0.1)'
                        : 'rgba(245, 158, 11, 0.08)',
                      border: '1px solid',
                      borderColor: (theme) => theme.palette.mode === 'dark'
                        ? 'rgba(245, 158, 11, 0.3)'
                        : 'rgba(245, 158, 11, 0.2)',
                      display: 'flex',
                      gap: 1.5,
                      alignItems: 'flex-start',
                    }}
                  >
                    <TipsAndUpdatesIcon sx={{ color: '#F59E0B', fontSize: 20, mt: 0.25 }} />
                    <Typography variant="caption" sx={{ color: 'text.primary', lineHeight: 1.6 }}>
                      {tutorialStep === 0 && t.tutorialStep1Tip}
                      {tutorialStep === 1 && t.tutorialStep2Tip}
                      {tutorialStep === 2 && t.tutorialStep3Tip}
                      {tutorialStep === 3 && t.tutorialStep4Tip}
                    </Typography>
                  </Box>
                )}

                {/* Visual Guide for specific steps */}
                {tutorialStep === 0 && (
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap' }}>
                    {[
                      { icon: <FoundationIcon />, label: language === 'my' ? 'Asas' : 'Foundation', color: '#059669' },
                      { icon: <GridOnIcon />, label: language === 'my' ? 'Dinding' : 'Walls', color: '#4F46E5' },
                      { icon: <ViewModuleIcon />, label: language === 'my' ? 'Lantai' : 'Floors', color: '#0891B2' },
                      { icon: <RoofingIcon />, label: language === 'my' ? 'Bumbung' : 'Roofing', color: '#DC2626' },
                    ].map((item, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: 0.5,
                          p: 1,
                        }}
                      >
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 2,
                            bgcolor: `${item.color}15`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: item.color,
                          }}
                        >
                          {item.icon}
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {item.label}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}

                {tutorialStep === 3 && (
                  <Box sx={{ mt: 2 }}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor: 'primary.main',
                        color: 'white',
                        textAlign: 'center',
                      }}
                    >
                      <Typography variant="caption" sx={{ opacity: 0.9 }}>
                        {language === 'my' ? 'Pasir' : 'Sand'}
                      </Typography>
                      <Typography variant="h5" fontWeight={700}>
                        2.5 m³
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.85 }}>
                        = 3.75 ton / 88.3 cuft
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5, justifyContent: 'center' }}>
                      <ShoppingCartIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="caption" color="text.secondary">
                        {language === 'my' ? 'Mudah untuk memesan dari pembekal!' : 'Easy to order from suppliers!'}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            </Fade>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, pt: 0, gap: 1 }}>
          {tutorialStep > 0 ? (
            <Button
              onClick={handleTutorialBack}
              startIcon={<ArrowBackIcon />}
              sx={{ color: 'text.secondary' }}
            >
              {t.tutorialBack}
            </Button>
          ) : (
            <Button
              onClick={handleTutorialClose}
              sx={{ color: 'text.secondary' }}
            >
              {t.tutorialSkip}
            </Button>
          )}
          <Box sx={{ flex: 1 }} />
          <Button
            variant="contained"
            onClick={handleTutorialNext}
            endIcon={tutorialStep < 4 ? <ArrowForwardIcon /> : null}
            sx={{ px: 3 }}
          >
            {tutorialStep < 4 ? t.tutorialNext : t.tutorialDone}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LandingPage;
